import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Mail, Globe, Lock, Database, Cookie, Eye, FileText, Megaphone } from 'lucide-react';
import Footer from '@/components/Footer';

export default function Privacy() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-grow max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/" className="text-blue-600 hover:text-blue-700 mb-4 inline-block">
            ← {t('common.back', 'Zurück')}
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Datenschutzerklärung</h1>
          <p className="text-gray-600">Angaben gemäß Datenschutz-Grundverordnung (DSGVO)</p>
          <p className="text-sm text-gray-500 mt-2">
            <strong>Stand:</strong> Januar 2025
          </p>
        </div>

        <div className="space-y-6">
          {/* 1. Verantwortlicher */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                1. Verantwortlicher
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="font-semibold text-gray-900">Wilhelm Wagner</p>
              <p className="text-gray-700">Einzelunternehmen</p>
              <p className="text-gray-700">Raffelspergergasse 11/2/2</p>
              <p className="text-gray-700">1190 Wien, Österreich</p>
              <p className="text-gray-700 mt-3">
                <strong>E-Mail:</strong>{' '}
                <a href="mailto:admin@ivoliq.io" className="text-blue-600 hover:underline">
                  admin@ivoliq.io
                </a>
              </p>
              <p className="text-gray-700">
                <strong>Website:</strong>{' '}
                <a href="https://www.ivoliq.io" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                  www.ivoliq.io
                </a>
              </p>
              <p className="text-gray-700">
                <strong>UID-Nummer:</strong> ATU80103969
              </p>
            </CardContent>
          </Card>

          {/* 2. Allgemeines */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                2. Allgemeines zur Datenverarbeitung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Umfang der Verarbeitung</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Wir verarbeiten personenbezogene Daten unserer Nutzer nur, soweit dies zur Bereitstellung einer funktionsfähigen Website und unserer Inhalte und Leistungen erforderlich ist.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlagen</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</li>
                  <li>Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</li>
                  <li>Art. 6 Abs. 1 lit. f DSGVO (Berechtigtes Interesse)</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 3. Website und Logfiles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-600" />
                3. Bereitstellung der Website
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Erhobene Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>IP-Adresse des Nutzers</li>
                  <li>Datum und Uhrzeit des Zugriffs</li>
                  <li>Browsertyp und -version</li>
                  <li>Betriebssystem</li>
                  <li>Referrer URL</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Gewährleistung der Systemsicherheit und eines reibungslosen Verbindungsaufbaus</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. f DSGVO (Berechtigtes Interesse)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">7 Tage, dann automatische Löschung</p>
              </div>
            </CardContent>
          </Card>

          {/* 4. Registrierung */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-blue-600" />
                4. Registrierung und Nutzerkonto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>E-Mail-Adresse (Pflicht)</li>
                  <li>Name (optional)</li>
                  <li>Passwort (verschlüsselt)</li>
                  <li>{t('privacy.taxResidency', 'Country of tax residency')}</li>
                  <li>Registrierungszeitpunkt</li>
                  <li>Plan/Abonnement</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">{t('privacy.dataUsageAccount', 'To manage user accounts and provide contractual services (tax calculations, project management, time tracking)')}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Bis zur Löschung des Nutzerkontos durch den Nutzer</p>
              </div>
            </CardContent>
          </Card>

          {/* 5. Cookies */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="h-5 w-5 text-blue-600" />
                5. Cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">5.1 Notwendige Cookies (ohne Einwilligung)</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Authentifizierungs-Token:</strong> Zur Speicherung Ihrer Login-Session
                </p>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Speicherdauer:</strong> 7 Tage oder bis zum Logout
                </p>
              </div>
              <div className="pt-4 border-t">
                <h4 className="font-semibold text-gray-900 mb-3">5.2 Analytics Cookies (mit Einwilligung)</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>PostHog:</strong> Zur Analyse des Nutzerverhaltens und Verbesserung der Website
                </p>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
                </p>
                <p className="text-gray-700 text-sm">
                  <strong>Speicherdauer:</strong> Bis zu 2 Jahre
                </p>
              </div>
              <div className="pt-4 border-t">
                <h4 className="font-semibold text-gray-900 mb-3">5.3 Marketing Cookies (mit Einwilligung)</h4>
                <p className="text-gray-700 text-sm mb-2">
                  <strong>Google Ads Conversion-Tracking:</strong> Zur Messung der Wirksamkeit unserer Werbekampagnen
                </p>
                <div className="text-sm text-gray-700 space-y-2 mb-3">
                  <div>
                    <strong>Cookies:</strong> _gcl_au, _gac_GB-* (Google Ads Cookies)
                  </div>
                  <div>
                    <strong>Speicherdauer:</strong> 90 Tage
                  </div>
                  <div>
                    <strong>Anbieter:</strong> Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland
                  </div>
                  <div>
                    <strong>Zweck:</strong> Conversion-Messung, Werbe-Erfolg tracken
                  </div>
                  <div>
                    <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)
                  </div>
                </div>
                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-800 mb-2">
                    <strong>⚠️ Hinweis zur Datenübermittlung in die USA:</strong>
                  </p>
                  <p className="text-sm text-orange-800">
                    Google LLC (Muttergesellschaft von Google Ireland) verarbeitet Daten in den USA. Google ist nach dem EU-US Data Privacy Framework zertifiziert.
                  </p>
                </div>
                <p className="text-sm text-gray-700 mt-3">
                  Weitere Informationen: <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Datenschutzerklärung</a>
                </p>
              </div>
              <div className="pt-4 border-t">
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Hinweis:</strong> Sie können Ihre Einwilligung jederzeit widerrufen über unsere Cookie-Einstellungen oder durch Löschen der Cookies in Ihren Browser-Einstellungen.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 6. Analytics */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" />
                6. Web-Analyse (PostHog)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">PostHog Inc., 2261 Market Street #4008, San Francisco, CA 94114, USA</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>IP-Adresse (anonymisiert)</li>
                  <li>Browserinformationen</li>
                  <li>Besuchte Seiten und Verweildauer</li>
                  <li>Klickverhalten</li>
                  <li>Gerätetyp</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Analyse des Nutzerverhaltens zur Verbesserung der Website und Optimierung der User Experience</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</p>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Datenverarbeitung in der EU:</strong>
                </p>
                <p className="text-sm text-blue-800">
                  Wir nutzen die EU-Server von PostHog (eu.i.posthog.com). Ihre Daten werden in Europa verarbeitet und gespeichert.
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen: <a href="https://posthog.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://posthog.com/privacy</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 7. Google Ads Tracking */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-orange-600" />
                7. Google Ads Conversion-Tracking
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Beschreibung</h4>
                <p className="text-gray-700 text-sm">
                  Wir nutzen Google Ads Conversion-Tracking, um die Wirksamkeit unserer Werbekampagnen zu messen. Dabei wird erfasst, ob Sie über eine Google-Anzeige auf unsere Website gelangt sind und anschließend eine bestimmte Aktion durchgeführt haben (z.B. Registrierung).
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">
                  Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland<br />
                  Muttergesellschaft: Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, USA
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>IP-Adresse (anonymisiert)</li>
                  <li>Klick-Informationen (Anzeigen-ID, Keywords)</li>
                  <li>Besuchte Seiten</li>
                  <li>Conversion-Events (z.B. Registrierung)</li>
                  <li>Gerätetyp und Browserinformationen</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">
                  Messung der Wirksamkeit unserer Google Ads Kampagnen, Optimierung der Werbeausgaben, Erfolgskontrolle
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung über Cookie-Banner)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">
                  Cookies: 90 Tage<br />
                  Conversion-Daten bei Google: Gemäß Google Ads Richtlinien
                </p>
              </div>
              <div className="p-4 bg-orange-50 border border-orange-200 rounded">
                <p className="text-sm text-orange-800 mb-2">
                  <strong>⚠️ Datenübermittlung in die USA:</strong>
                </p>
                <p className="text-sm text-orange-800">
                  Google LLC verarbeitet Daten auch in den USA. Google ist nach dem EU-US Data Privacy Framework zertifiziert, welches ein angemessenes Datenschutzniveau gewährleistet.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Widerruf</h4>
                <p className="text-gray-700 text-sm">
                  Sie können Ihre Einwilligung jederzeit über die Cookie-Einstellungen (Footer-Link) widerrufen oder Cookies in Ihren Browser-Einstellungen löschen.
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen:{' '}
                  <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Google Datenschutzerklärung
                  </a>
                  {' | '}
                  <a href="https://support.google.com/google-ads/answer/2407785" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    Google Ads Tracking
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 8. Google OAuth */}
          <Card>
            <CardHeader>
              <CardTitle>8. Google OAuth (Login)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>Google-ID</li>
                  <li>E-Mail-Adresse</li>
                  <li>Name</li>
                  <li>Profilbild (optional)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Vereinfachte Registrierung und Anmeldung</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Bis zur Kontolöschung oder Widerruf der OAuth-Berechtigung über Ihr Google-Konto</p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen: <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Datenschutzerklärung</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 9. Stripe */}
          <Card>
            <CardHeader>
              <CardTitle>9. Zahlungsdienste (Stripe)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">Stripe Payments Europe Ltd., 1 Grand Canal Street Lower, Grand Canal Dock, Dublin, D02 H210, Irland</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>Name und E-Mail-Adresse</li>
                  <li>Rechnungsadresse</li>
                  <li>Zahlungsinformationen (Kreditkarte, IBAN)</li>
                  <li>Transaktionsdaten</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Abwicklung von Zahlungsvorgängen für kostenpflichtige Dienste</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Gemäß gesetzlicher Aufbewahrungsfristen (7 Jahre für Buchhaltungsdaten)</p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen: <a href="https://stripe.com/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 10. App-Nutzungsdaten */}
          <Card>
            <CardHeader>
              <CardTitle>10. App-Nutzungsdaten (Ihre Geschäftsdaten)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Kundendaten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Name, Adresse, E-Mail, Telefonnummer Ihrer Kunden</li>
                      <li>UID-Nummer / Umsatzsteuer-Identifikationsnummer</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Projektdaten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Projektname, Beschreibung</li>
                      <li>Stundensätze, Budget</li>
                      <li>Projektstatus</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Zeiterfassungen:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Datum, Uhrzeit (Start/Ende), Dauer</li>
                      <li>Tätigkeitsbeschreibung</li>
                      <li>Projektzuordnung</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Rechnungsdaten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Rechnungsnummer, Beträge (Netto/Brutto)</li>
                      <li>Rechnungspositionen, Steuersätze</li>
                      <li>Zahlungsstatus, Fälligkeitsdatum</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800 mb-1">Finanzdaten:</p>
                    <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                      <li>Einnahmen und Ausgaben</li>
                      <li>Kategorien, Belege</li>
                      <li>Steuerberechnungen</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Bereitstellung der SaaS-Funktionalität zur Verwaltung Ihrer Geschäftsdaten (Zeiterfassung, Kundenverwaltung, Rechnungserstellung, Steuerkalkulation)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Bis zur Kontolöschung. <strong>Ausnahme:</strong> Rechnungsdaten werden gemäß § 147 AO (DE) bzw. § 132 BAO (AT) für 7 Jahre aufbewahrt (gesetzliche Aufbewahrungspflicht).</p>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Wichtig:</strong> Sie sind für die von Ihnen eingegebenen Kundendaten selbst verantwortlich (Sie sind der Verantwortliche i.S.d. DSGVO). ivoliq tritt als Auftragsverarbeiter auf. Bitte beachten Sie unseren Auftragsverarbeitungsvertrag (AVV).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 11. IP-Geolocation */}
          <Card>
            <CardHeader>
              <CardTitle>11. IP-Geolocation (Standorterkennung)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('common.description')}</h4>
                <p className="text-gray-700 text-sm">{t('privacy.dataUsageGeolocation', 'We use IP geolocation to automatically detect your country for correct application of tax laws.')}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>IP-Adresse (nicht gespeichert)</li>
                  <li>Erkanntes Land (gespeichert)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">{t('privacy.dataUsageAutoDetect', 'Automatic detection of tax residency for accurate tax calculations')}</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. f DSGVO (Berechtigtes Interesse)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Speicherdauer</h4>
                <p className="text-gray-700 text-sm">Die IP-Adresse wird nicht gespeichert. Der ermittelte Ländercode wird im Benutzerkonto gespeichert, bis der Nutzer sein Konto löscht.</p>
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800">
                  <strong>Hinweis:</strong> Ihre IP-Adresse wird nicht gespeichert, nur der erkannte Land-Code wird in Ihrem Profil hinterlegt.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 12. Hosting */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-blue-600" />
                12. Hosting
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Anbieter</h4>
                <p className="text-gray-700 text-sm">Hetzner Online GmbH, Industriestr. 25, 91710 Gunzenhausen, Deutschland</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Verarbeitete Daten</h4>
                <p className="text-gray-700 text-sm">Alle Daten, die auf unserer Website verarbeitet werden</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Zweck</h4>
                <p className="text-gray-700 text-sm">Sicheres und zuverlässiges Hosting unserer Website</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Rechtsgrundlage</h4>
                <p className="text-gray-700 text-sm">Art. 6 Abs. 1 lit. f DSGVO (Berechtigtes Interesse)</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Auftragsverarbeitung</h4>
                <p className="text-gray-700 text-sm">Mit Hetzner wurde ein Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO geschlossen.</p>
              </div>
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800">
                  <strong>Serverstandort:</strong> Deutschland / EU
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Weitere Informationen: <a href="https://www.hetzner.com/rechtliches/datenschutz" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Hetzner Datenschutzerklärung</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 13. Betroffenenrechte */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-blue-600" />
                13. Ihre Rechte (Betroffenenrechte)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Sie haben folgende Rechte:</h4>
                  <ul className="space-y-3 text-sm text-gray-700">
                    <li>
                      <strong>Recht auf Auskunft (Art. 15 DSGVO):</strong><br />
                      Sie haben das Recht, Auskunft über Ihre gespeicherten personenbezogenen Daten zu erhalten.
                    </li>
                    <li>
                      <strong>Recht auf Berichtigung (Art. 16 DSGVO):</strong><br />
                      Sie haben das Recht, unrichtige Daten berichtigen zu lassen.
                    </li>
                    <li>
                      <strong>Recht auf Löschung (Art. 17 DSGVO):</strong><br />
                      Sie haben das Recht, Ihre Daten löschen zu lassen ("Recht auf Vergessenwerden").
                      <div className="mt-2 ml-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                        <p className="font-semibold mb-2">Löschprozess:</p>
                        <ol className="list-decimal list-inside space-y-1">
                          <li>Sie können Ihr Konto jederzeit über die Einstellungen löschen</li>
                          <li>Nach Löschung: 30 Tage Wiederherstellungsfrist (Soft-Delete)</li>
                          <li>Nach Ablauf der Frist: Endgültige Löschung aller Daten binnen 7 Tagen</li>
                          <li><strong>Ausnahme:</strong> Rechnungsdaten werden gemäß § 147 AO (DE) bzw. § 132 BAO (AT) für 7 Jahre aufbewahrt (gesetzliche Aufbewahrungspflicht)</li>
                          <li>Logfiles werden nach 7 Tagen automatisch gelöscht</li>
                        </ol>
                      </div>
                    </li>
                    <li>
                      <strong>Recht auf Einschränkung (Art. 18 DSGVO):</strong><br />
                      Sie haben das Recht, die Verarbeitung einschränken zu lassen.
                    </li>
                    <li>
                      <strong>Recht auf Datenübertragbarkeit (Art. 20 DSGVO):</strong><br />
                      Sie haben das Recht, Ihre Daten in einem strukturierten Format zu erhalten.
                    </li>
                    <li>
                      <strong>Widerspruchsrecht (Art. 21 DSGVO):</strong><br />
                      Sie haben das Recht, der Datenverarbeitung zu widersprechen.
                    </li>
                    <li>
                      <strong>Recht auf Widerruf (Art. 7 DSGVO):</strong><br />
                      Sie können erteilte Einwilligungen jederzeit widerrufen.
                    </li>
                    <li>
                      <strong>Beschwerderecht (Art. 77 DSGVO):</strong><br />
                      Sie haben das Recht, sich bei einer Aufsichtsbehörde zu beschweren.
                    </li>
                  </ul>
                </div>
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-gray-900 mb-2">Kontakt zur Ausübung Ihrer Rechte:</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    Senden Sie uns eine E-Mail an:{' '}
                    <a href="mailto:admin@ivoliq.io" className="text-blue-600 hover:underline font-medium">
                      admin@ivoliq.io
                    </a>
                  </p>
                  <p className="text-sm text-gray-700">
                    Sie können Ihr Konto auch direkt in den Einstellungen löschen.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 14. Aufsichtsbehörde */}
          <Card>
            <CardHeader>
              <CardTitle>14. Beschwerderecht bei der Aufsichtsbehörde</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-gray-700 text-sm">
                Sie haben das Recht, sich bei der zuständigen Datenschutz-Aufsichtsbehörde zu beschweren:
              </p>
              <div className="p-4 bg-gray-50 border border-gray-200 rounded">
                <p className="font-semibold text-gray-900">Österreichische Datenschutzbehörde</p>
                <p className="text-sm text-gray-700">Barichgasse 40-42</p>
                <p className="text-sm text-gray-700">1030 Wien, Österreich</p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>Telefon:</strong> +43 1 52 152-0
                </p>
                <p className="text-sm text-gray-700">
                  <strong>E-Mail:</strong>{' '}
                  <a href="mailto:dsb@dsb.gv.at" className="text-blue-600 hover:underline">
                    dsb@dsb.gv.at
                  </a>
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Website:</strong>{' '}
                  <a href="https://www.dsb.gv.at/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                    https://www.dsb.gv.at/
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 15. Datensicherheit */}
          <Card>
            <CardHeader>
              <CardTitle>15. Datensicherheit</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-gray-700 text-sm">
                Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um Ihre Daten gegen zufällige oder vorsätzliche Manipulationen, Verlust, Zerstörung oder den Zugriff unberechtigter Personen zu schützen.
              </p>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Unsere Maßnahmen:</h4>
                <ul className="list-disc ml-5 space-y-1 text-sm text-gray-700">
                  <li>SSL/TLS-Verschlüsselung (HTTPS)</li>
                  <li>Verschlüsselte Passwort-Speicherung (Hashing)</li>
                  <li>Zugriffskontrolle und Berechtigungskonzept</li>
                  <li>Regelmäßige Sicherheits-Updates</li>
                  <li>Firewall und Intrusion Detection</li>
                  <li>Regelmäßige Backups</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 16. Änderungen */}
          <Card>
            <CardHeader>
              <CardTitle>16. Änderungen dieser Datenschutzerklärung</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 text-sm">
                Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen. Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.
              </p>
            </CardContent>
          </Card>

          {/* Footer Info */}
          <div className="text-center py-6">
            <p className="text-sm text-gray-500">
              Bei Fragen zur Verarbeitung Ihrer Daten kontaktieren Sie uns unter:{' '}
              <a href="mailto:admin@ivoliq.io" className="text-blue-600 hover:underline font-medium">
                admin@ivoliq.io
              </a>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
